<template>
  <div class="dismissed-receipts">
    <div class="header">
      <button @click="$router.back()" class="btn-back">&larr; Back</button>
      <h1>Dismissed Receipts</h1>
    </div>
    <p class="description">
      These are email receipts that were dismissed (e.g. $0 orders fully covered by gift card or credit).
    </p>

    <div v-if="loading" class="loading">Loading...</div>

    <div v-else-if="transactions.length === 0" class="empty-state">
      <p>No dismissed receipts found.</p>
    </div>

    <div v-else class="receipts-list">
      <div
        v-for="txn in transactions"
        :key="txn.id"
        class="receipt-card"
      >
        <div class="receipt-content" @click="$router.push(`/transactions/${txn.id}`)">
          <div class="receipt-header">
            <span class="receipt-order">
              {{ txn.order_number ? `Order #${txn.order_number}` : 'Order (Unknown)' }}
            </span>
            <span class="receipt-amount">${{ formatAmount(txn.order_total) }}</span>
          </div>
          <div class="receipt-details">
            <span class="receipt-date">{{ formatDate(txn.order_date) }}</span>
            <span class="receipt-items">{{ txn.items?.length || 0 }} items</span>
          </div>
          <div v-if="txn.items && txn.items.length > 0" class="receipt-item-list">
            <span v-for="item in txn.items" :key="item.id" class="receipt-item-name">
              {{ item.name }}
            </span>
          </div>
        </div>
        <div class="receipt-actions">
          <button
            @click.stop="restoreReceipt(txn.id)"
            :disabled="restoringId === txn.id"
            class="btn-restore"
          >
            {{ restoringId === txn.id ? 'Restoring...' : 'Restore' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { transactionsAPI } from '../services/api'

const loading = ref(true)
const transactions = ref([])
const restoringId = ref(null)

onMounted(async () => {
  try {
    const response = await transactionsAPI.getAll({ status: 'dismissed', limit: 200 })
    transactions.value = response.data.transactions
  } catch (err) {
    console.error('Error loading dismissed receipts:', err)
  } finally {
    loading.value = false
  }
})

const restoreReceipt = async (id) => {
  restoringId.value = id
  try {
    await transactionsAPI.restore(id)
    // Remove from list after restoring
    transactions.value = transactions.value.filter(t => t.id !== id)
  } catch (err) {
    console.error('Error restoring receipt:', err)
    alert(err.response?.data?.error || 'Failed to restore receipt')
  } finally {
    restoringId.value = null
  }
}

const formatDate = (dateStr) => {
  if (!dateStr) return 'Date unknown'
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const formatAmount = (amount) => {
  if (amount === null || amount === undefined) return '—'
  return Number(amount).toFixed(2)
}
</script>

<style scoped>
.dismissed-receipts {
  max-width: 900px;
  margin: 0 auto;
}

.header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.header h1 {
  color: #2c3e50;
  margin: 0;
}

.btn-back {
  background: none;
  border: 1px solid #ddd;
  padding: 0.4rem 0.75rem;
  border-radius: 4px;
  cursor: pointer;
  color: #7f8c8d;
  font-size: 0.9rem;
}
.btn-back:hover {
  background: #f8f9fa;
  color: #2c3e50;
}

.description {
  color: #7f8c8d;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
}

.loading, .empty-state {
  text-align: center;
  padding: 3rem;
  color: #7f8c8d;
}

.receipts-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.receipt-card {
  background: white;
  border: 1px solid #ecf0f1;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  transition: all 0.15s;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.receipt-card:hover {
  border-color: #3498db;
  box-shadow: 0 2px 8px rgba(52, 152, 219, 0.15);
}

.receipt-content {
  flex: 1;
  cursor: pointer;
}

.receipt-actions {
  flex-shrink: 0;
}

.btn-restore {
  background-color: #27ae60;
  color: white;
  border: none;
  padding: 0.4rem 0.75rem;
  border-radius: 4px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-restore:hover:not(:disabled) {
  background-color: #219a52;
}

.btn-restore:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.receipt-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.4rem;
}

.receipt-order {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.95rem;
}

.receipt-amount {
  font-weight: 700;
  color: #7f8c8d;
  font-size: 0.95rem;
}

.receipt-details {
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  color: #7f8c8d;
  margin-bottom: 0.5rem;
}

.receipt-item-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.receipt-item-name {
  background: #f8f9fa;
  padding: 0.2rem 0.5rem;
  border-radius: 3px;
  font-size: 0.8rem;
  color: #2c3e50;
}
</style>
